import { InlineLoading, ProgressIndicator, ProgressStep } from '@carbon/react';
import { FC, Suspense } from 'react';

import { NavigationGuard } from 'components/navigation-guard/navigation-guard';

import { WizardStepContext } from './context';
import { useStepper } from './hooks';
import { StepperFooter } from './stepper-footer';
import { FullPageWizardProps } from './types';
import styles from './full-page-wizard.module.scss';

/**
 * Full-page stepper wizard with horizontal progress indicator,
 * step content area, and navigation footer.
 *
 * @param {FullPageWizardProps} props - Steps array and onCancel callback
 * @return {JSX.Element} The rendered wizard
 */
const FullPageWizard: FC<FullPageWizardProps> = ({ steps, onCancel }) => {
  const stepper = useStepper({ steps, onCancel });

  return (
    <div className={styles.wizard}>
      <NavigationGuard
        hasChanges={stepper.navigationGuardEnabled}
        message="You have unsaved changes. Are you sure you want to leave?"
      />

      <div className={styles.progressBarWrapper}>
        <div className={styles.progressBar}>
          <ProgressIndicator
            currentIndex={stepper.currentStep}
            onChange={stepper.goToStep}
            spaceEqually
          >
            {steps.map((step, i) => (
              <ProgressStep
                key={step.label}
                label={step.label}
                invalid={stepper.hasError && i === stepper.currentStep}
                disabled={
                  !stepper.completedSteps.has(i) && i !== stepper.currentStep
                }
              />
            ))}
          </ProgressIndicator>
        </div>
      </div>

      <div className={styles.content}>
        <WizardStepContext.Provider value={stepper.stepContextValue}>
          {/* Step content may read suspending async atoms (e.g. the CRM tract
              form). React 18 throws if a component suspends on a synchronous
              step change with no boundary, so wrap the active step in Suspense
              and show a loader until its data resolves. */}
          <Suspense fallback={<InlineLoading description="Loading..." />}>
            {steps[stepper.currentStep].content}
          </Suspense>
        </WizardStepContext.Provider>
      </div>

      <StepperFooter
        isNextEnabled={stepper.isNextEnabled}
        isBackEnabled={stepper.isBackEnabled}
        loadingAction={stepper.loadingAction}
        nextLabel={steps[stepper.currentStep].nextLabel || 'Next'}
        onCancel={stepper.handleCancel}
        onBack={stepper.handleBack}
        onNext={stepper.handleNext}
      />
    </div>
  );
};

export { FullPageWizard };
